// @flow
import React from 'react';
import { get, keys, map, size } from 'lodash';
import { SettingsMenu, PlatformSwitch, AppMobileHeader, ConditionalFeature, AppMobileFooter } from '@blocks';
import {
  Breadcrumb, Button, ClickableList,
  Loading,
  ProgressBar,
} from '@elements';
import type { DrugsPrescriptionsTemplatesState, User, BundledPermissions } from '@types';
import { sort, alphabeticCompare, createNsTranslator } from '@helpers/helpers';
import { withTranslation } from 'react-i18next';
import TemplatesEmptyState from '../TemplatesEmptyState';

type Props = {
  drugsPrescriptionsTemplates: DrugsPrescriptionsTemplatesState,
  user: User,
  onLogout: Function,
  t: Function,
  permissions: BundledPermissions,
  featureEnabled: boolean,
};
class DrugsPrescriptionsTemplatesList extends React.Component<Props> {
  render() {
    const {
      drugsPrescriptionsTemplates, user, onLogout, featureEnabled,
    } = this.props;

    const _ = createNsTranslator('settings', this.props.t);

    const breadcrumbLinks = [
      { text: _('label/prescription_menu/title'), linkTo: '/ajustes/modelos' },
      { text: _('label/drugs_prescription_teplates/list/title') },
    ];

    const templateIds = keys(drugsPrescriptionsTemplates.data);
    const templateItems = sort(map(templateIds, (templateId) => {
      const template = get(drugsPrescriptionsTemplates, ['data', templateId]);
      return {
        text: template.name,
        linkTo: `/ajustes/modelos/receituarios/${template.id}`,
      };
    },
    ), alphabeticCompare('text'));

    const templatesLoading = get(drugsPrescriptionsTemplates, ['isLoading']);
    const hasTemplates = templateItems.length > 0;
    // A failed load leaves the list empty too, which is not the same as having none
    const loadFailed = !hasTemplates && !templatesLoading && size(get(drugsPrescriptionsTemplates, ['errors'])) > 0;
    const noTemplates = !hasTemplates && !templatesLoading && !loadFailed;
    // "Novo Modelo" waits for the list: with none, the empty state has its own button
    const showNewButton = hasTemplates || loadFailed;

    let noTemplatesContent;
    if (noTemplates) {
      noTemplatesContent = (
        <TemplatesEmptyState
          icon="medication"
          title={_('label/drugs_prescription_teplates/list/empty_title')}
          hint={_('label/drugs_prescription_teplates/list/empty_hint')}
          actionText={_('label/templates/empty_action')}
          linkTo="/ajustes/modelos/receituarios/novo"
        />
      );
    } else if (loadFailed) {
      noTemplatesContent = <p className="templatesLoadError" role="alert">{_('label/templates/load_error')}</p>;
    } else if (templatesLoading && templateItems.length === 0) {
      noTemplatesContent = (
        <div className='loadingWrapper'>
          <Loading />
        </div>
      );
    }

    const pageContent = (
      <React.Fragment>
        <div className={`toolbar dashed templatesToolbar${showNewButton ? '' : ' isEmpty'}`}>
          <h1>{_('label/drugs_prescription_teplates/list/title')}</h1>
          {showNewButton && (
            <Button
              className="primary"
              text={_('label/drugs_prescription_teplates/list/new')}
              linkTo="/ajustes/modelos/receituarios/novo"
            />
          )}
        </div>
        {noTemplatesContent}
        {templateItems.length > 0 && (
          <ClickableList
            items={templateItems}
            card
          />
        )}
      </React.Fragment>
    );

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <div id="drugsPrescriptionsTemplatesList" className="appContent settingsPage">
            {!noTemplates && templatesLoading && <ProgressBar show />}
            <aside className="sidebar">
              <SettingsMenu user={user} onLogout={onLogout} permissions={this.props.permissions} />
            </aside>
            <section className="mainSection">
              <ConditionalFeature enabled={featureEnabled}>
                <div className="centerContent">
                  <Breadcrumb items={breadcrumbLinks} />
                  {pageContent}
                </div>
              </ConditionalFeature>
            </section>
          </div>
        )}
        mobile={() => (
          <React.Fragment>
            <AppMobileHeader title={_('label/drugs_prescription_teplates/list/title')} backLinkTo="/ajustes/modelos" />
            <div id="drugsPrescriptionsTemplatesList" className="appContent settingsPage">
              {!noTemplates && templatesLoading && <ProgressBar show />}
              <section className="mainSection">
                <ConditionalFeature enabled={featureEnabled}>
                  <div className="centerContent">{pageContent}</div>
                </ConditionalFeature>
              </section>
            </div>
            <AppMobileFooter />
          </React.Fragment>
        )}
      />
    );
  }
}

export default withTranslation()(DrugsPrescriptionsTemplatesList);
